import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Users, UserCog, Heart, Gamepad2, ClipboardList, CheckCircle, Shield, LogIn } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

interface AdminStats {
  total_usuarios: number;
  total_profesionales: number;
  total_pacientes: number;
  total_individuales: number;
  total_admins: number;
  total_sesiones: number;
  sesiones_finalizadas: number;
}

interface User {
  id: number;
  username: string;
  email: string;
  rol: string;
  fecha_registro: Date;
  first_name: string | null;
  last_name: string | null;
  avatar: string | null;
  num_pacientes?: number;
  profesional_username?: string;
  profesional_nombre?: string;
}

interface AdminLog {
  id: number;
  admin_username: string;
  afectado_username: string | null;
  accion: string;
  detalles: string | null;
  created_at: Date;
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [individuales, setIndividuales] = useState<User[]>([]);
  const [profesionales, setProfesionales] = useState<User[]>([]);
  const [pacientes, setPacientes] = useState<User[]>([]);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'individuales' | 'profesionales' | 'pacientes' | 'logs'>('individuales');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [targetRole, setTargetRole] = useState<'profesional' | 'paciente' | null>(null);
  const { setUser } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadStats(),
        loadIndividuales(),
        loadProfesionales(),
        loadPacientes(),
        loadLogs(),
      ]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    const response = await api.get('/admin/stats');
    if (response.data.ok) {
      setStats(response.data.data);
    }
  };

  const loadIndividuales = async () => {
    const response = await api.get('/admin/users?role=individual');
    if (response.data.ok) {
      setIndividuales(response.data.data);
    }
  };

  const loadProfesionales = async () => {
    const response = await api.get('/admin/users?role=profesional');
    if (response.data.ok) {
      setProfesionales(response.data.data);
    }
  };

  const loadPacientes = async () => {
    const response = await api.get('/admin/users?role=paciente');
    if (response.data.ok) {
      setPacientes(response.data.data);
    }
  };

  const loadLogs = async () => {
    const response = await api.get('/admin/logs?limit=20');
    if (response.data.ok) {
      setLogs(response.data.data);
    }
  };

  const handleImpersonate = async (user: User) => {
    try {
      const res = await api.post(`/admin/impersonate/${user.id}`);
      if (res.data.ok) {
        setUser(res.data.data.user);
        navigate(user.rol === 'profesional' ? '/profesional/pacientes' : '/juegos');
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'No se pudo impersonar al usuario');
    }
  };

  const handleConvertUser = (user: User, role: 'profesional' | 'paciente') => {
    setSelectedUser(user);
    setTargetRole(role);
    setShowConvertModal(true);
  };

  const confirmConvert = async () => {
    if (!selectedUser || !targetRole) return;

    try {
      const response = await api.patch(`/admin/users/${selectedUser.id}/role`, {
        newRole: targetRole,
      });

      if (response.data.ok) {
        setShowConvertModal(false);
        setSelectedUser(null);
        setTargetRole(null);
        await loadData();
      }
    } catch (error) {
      console.error('Error al convertir usuario:', error);
      alert('Error al convertir usuario');
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getUserDisplayName = (user: User) => {
    return user.first_name
      ? `${user.first_name} ${user.last_name || ''}`.trim()
      : user.username;
  };

  const statCards = [
    {
      title: 'Total Usuarios',
      value: stats?.total_usuarios || 0,
      icon: Users,
      color: 'text-teal-400',
      bgColor: 'bg-teal-500/10',
      borderColor: 'border-teal-500/30',
    },
    {
      title: 'Profesionales',
      value: stats?.total_profesionales || 0,
      icon: UserCog,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
    },
    {
      title: 'Pacientes',
      value: stats?.total_pacientes || 0,
      icon: Heart,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
    },
    {
      title: 'Usuarios Libres',
      value: stats?.total_individuales || 0,
      icon: Gamepad2,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
    },
    {
      title: 'Administradores',
      value: stats?.total_admins || 0,
      icon: Shield,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
    },
    {
      title: 'Sesiones Creadas',
      value: stats?.total_sesiones || 0,
      icon: ClipboardList,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
    },
    {
      title: 'Sesiones Finalizadas',
      value: stats?.sesiones_finalizadas || 0,
      icon: CheckCircle,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
    },
  ];

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-10 h-10 text-neureka-yellow" />
            <h1 className="text-4xl font-display font-bold">Panel de Administración</h1>
          </div>
          <p className="text-white/70 text-lg">Gestión completa del sistema Neureka</p>
        </motion.div>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
            <p className="mt-4 text-white/70">Cargando datos...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {statCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-container border-2 ${card.borderColor} group hover:scale-105 transition-transform`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${card.bgColor}`}>
                      <card.icon className={`w-6 h-6 ${card.color}`} />
                    </div>
                    <div className={`text-3xl font-bold ${card.color}`}>
                      {card.value}
                    </div>
                  </div>
                  <h3 className="text-white/70 text-sm font-medium">
                    {card.title}
                  </h3>
                </motion.div>
              ))}
            </div>

            {/* Tabs Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-container"
            >
              <h2 className="text-2xl font-display font-bold mb-6">
                Gestión de Usuarios
              </h2>

              {/* Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {[
                  { id: 'individuales' as const, label: `🎮 Usuarios Libres (${individuales.length})` },
                  { id: 'profesionales' as const, label: `👨‍⚕️ Profesionales (${profesionales.length})` },
                  { id: 'pacientes' as const, label: `🏥 Pacientes (${pacientes.length})` },
                  { id: 'logs' as const, label: '📜 Logs de Auditoría' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-neureka-yellow text-neureka-purple'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content - Usuarios Individuales */}
              {activeTab === 'individuales' && (
                <div className="space-y-3">
                  {individuales.length === 0 ? (
                    <div className="text-center py-12 text-white/50">
                      <Gamepad2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No hay usuarios libres</p>
                    </div>
                  ) : (
                    individuales.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-4xl">
                            {user.avatar || '👤'}
                          </div>
                          <div>
                            <div className="font-semibold">{getUserDisplayName(user)}</div>
                            <div className="text-sm text-white/60">{user.email}</div>
                            <div className="text-xs text-white/40">
                              Registro: {formatDate(user.fecha_registro)}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleImpersonate(user)}
                            className="p-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
                            title="Entrar como este usuario"
                          >
                            <LogIn className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleConvertUser(user, 'profesional')}
                            className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors text-sm font-semibold"
                          >
                            → Profesional
                          </button>
                          <button
                            onClick={() => handleConvertUser(user, 'paciente')}
                            className="px-4 py-2 rounded-lg bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-colors text-sm font-semibold"
                          >
                            → Paciente
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Tab Content - Profesionales */}
              {activeTab === 'profesionales' && (
                <div className="space-y-3">
                  {profesionales.length === 0 ? (
                    <div className="text-center py-12 text-white/50">
                      <UserCog className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No hay profesionales registrados</p>
                    </div>
                  ) : (
                    profesionales.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-white/5"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-4xl">
                            {user.avatar || '👨‍⚕️'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{getUserDisplayName(user)}</span>
                              <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300">
                                Profesional
                              </span>
                            </div>
                            <div className="text-sm text-white/60">{user.email}</div>
                            <div className="text-xs text-white/40">
                              Pacientes: {user.num_pacientes || 0} | Registro: {formatDate(user.fecha_registro)}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleImpersonate(user)}
                          className="p-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
                          title="Entrar como este profesional"
                        >
                          <LogIn className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Tab Content - Pacientes */}
              {activeTab === 'pacientes' && (
                <div className="space-y-3">
                  {pacientes.length === 0 ? (
                    <div className="text-center py-12 text-white/50">
                      <Heart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No hay pacientes registrados</p>
                    </div>
                  ) : (
                    pacientes.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-white/5"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-4xl">
                            {user.avatar || '🏥'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{getUserDisplayName(user)}</span>
                              <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-300">
                                Paciente
                              </span>
                            </div>
                            <div className="text-sm text-white/60">{user.email}</div>
                            <div className="text-xs text-white/40">
                              Profesional: {user.profesional_nombre || user.profesional_username || '⚠️ Sin asignar'} | Registro: {formatDate(user.fecha_registro)}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleImpersonate(user)}
                          className="p-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
                          title="Entrar como este paciente"
                        >
                          <LogIn className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Tab Content - Logs */}
              {activeTab === 'logs' && (
                <div className="space-y-2">
                  {logs.length === 0 ? (
                    <div className="text-center py-12 text-white/50">
                      <ClipboardList className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No hay actividad registrada</p>
                    </div>
                  ) : (
                    logs.map((log) => (
                      <div
                        key={log.id}
                        className="flex justify-between items-center p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div>
                          <div className="font-medium">
                            {log.accion === 'cambiar_rol' && `🔄 Cambio de rol: ${log.afectado_username}`}
                            {log.accion === 'convertir_profesional' && `👨‍⚕️ Convertido a profesional: ${log.afectado_username}`}
                            {log.accion === 'asignar_paciente' && `🏥 Asignado paciente: ${log.afectado_username}`}
                            {!['cambiar_rol', 'convertir_profesional', 'asignar_paciente'].includes(log.accion) && log.accion}
                          </div>
                          {log.detalles && (
                            <div className="text-sm text-white/50">{log.detalles}</div>
                          )}
                          <div className="text-xs text-white/40 mt-1">
                            Por: {log.admin_username} • {new Date(log.created_at).toLocaleString('es-ES')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}

        {/* Confirmation Modal */}
        {showConvertModal && selectedUser && targetRole && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowConvertModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-container max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold mb-4">
                🔄 Confirmar Conversión
              </h3>
              <div className="mb-6 p-4 rounded-lg bg-white/5 text-center">
                <div className="text-4xl mb-2">{selectedUser.avatar || '👤'}</div>
                <div className="font-semibold">{getUserDisplayName(selectedUser)}</div>
                <div className="text-sm text-white/60">{selectedUser.email}</div>
                <div className="mt-3 text-sm">
                  <span className="text-white/70">Rol actual: </span>
                  <span className="font-semibold">{selectedUser.rol}</span>
                  {' → '}
                  <span className="font-semibold text-neureka-yellow">{targetRole}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConvertModal(false)}
                  className="flex-1 btn-neureka-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmConvert}
                  className="flex-1 btn-neureka"
                >
                  ✅ Confirmar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
